import { DesktopWindowsOutlined, PhoneIphoneOutlined } from '@mui/icons-material'
import { Stack, Typography } from '@mui/material'
import { ReactNode, useContext } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { FlexBox, GapBox } from 'components/Box'
import { BoxForm, NumberField, Switch } from 'components/Form'
import { SectionHeader } from 'components/Header'
import { Link } from 'components/Link'
import Paper from 'components/Paper'
import { AddEditAdUnitContext } from 'features/inventory/ad-unit/contexts/AddEditAdUnitContext'
import { RefreshIntervalSoftValidator } from 'features/inventory/domain/forms/RefresherForm/components/BasicSettingsSection'
import { useAbility } from 'providers/casl'
import { CONTAINER_SPACING } from 'providers/material-ui/theme/constants'
import { URL } from '../constants'
import { Schema } from '../schema'
import { BasicSettingsDomainRelated } from './BasicSettingsDomainRelated'

export const BasicSettingsSection = (): JSX.Element => {
  const ability = useAbility()
  const { t } = useTranslation('features/adUnit', { keyPrefix: 'form.refresher.basicSettings' })
  const { t: tc } = useTranslation('common')

  const { adUnit } = useContext(AddEditAdUnitContext)

  if (adUnit === undefined) {
    throw Error('Ad unit should be accessible')
  }

  const {
    domain: {
      domainRefresher: {
        desktopAdThreshold: domainDesktopAdThreshold,
        desktopRefreshInterval: domainDesktopRefreshInterval,
        mobileAdThreshold: domainMobileAdThreshold,
        mobileRefreshInterval: domainMobileRefreshInterval,
      },
    },
  } = adUnit

  const { control } = useFormContext<Schema>()
  const { basicSettings } = useWatch()

  const cannotUpdateAdUnit: boolean = ability.cannot('update', 'AdUnit')

  const DesktopRefreshIntervalSoftValidator: ReactNode = RefreshIntervalSoftValidator<Schema>({
    path: 'basicSettings.desktopRefreshInterval',
  })
  const MobileRefreshIntervalSoftValidator: ReactNode = RefreshIntervalSoftValidator<Schema>({
    path: 'basicSettings.mobileRefreshInterval',
  })

  return (
    <Paper data-cy='basic-settings-section'>
      <SectionHeader
        subtitle={t('subtitle')}
        subtitleEndAdornment={
          <Link
            newTab
            to={URL.KNOWLEDGE_BASE.BASIC_SETTINGS}
          >
            {tc('learnMore')}
          </Link>
        }
        title={t('title')}
      />

      <GapBox />
      <Switch
        control={control}
        disabled={cannotUpdateAdUnit}
        label={t('customizeRefresherBasicSettingsEnabled.label')}
        labelPlacement='start'
        name='basicSettings.customizeRefresherBasicSettingsEnabled'
        tooltip={{ content: t('customizeRefresherBasicSettingsEnabled.tooltip') }}
      />
      <GapBox />

      {basicSettings?.customizeRefresherBasicSettingsEnabled && (
        <Stack
          direction='row'
          ml={CONTAINER_SPACING}
          spacing={8}
        >
          <BoxForm>
            <FlexBox
              axis='x'
              gap={1}
            >
              <DesktopWindowsOutlined fontSize='inherit' />
              <Typography>{tc('device.desktop')}</Typography>
            </FlexBox>

            <NumberField
              control={control}
              disabled={cannotUpdateAdUnit}
              fixedHelperText
              helperText={DesktopRefreshIntervalSoftValidator}
              label={t('columns.refreshInterval.label')}
              name='basicSettings.desktopRefreshInterval'
              tooltip={{ content: t('columns.refreshInterval.tooltip') }}
              width='md'
            />

            <NumberField
              control={control}
              disabled={cannotUpdateAdUnit}
              fixedHelperText
              label={t('columns.adThreshold.label')}
              name='basicSettings.desktopAdThreshold'
              tooltip={{ content: t('columns.adThreshold.tooltip') }}
              width='md'
            />

            <Switch
              control={control}
              disabled={cannotUpdateAdUnit}
              label={t('columns.monitorUserScrollEnabled.label')}
              labelPlacement='start'
              name='basicSettings.desktopMonitorUserScrollEnabled'
              tooltip={{ content: t('columns.monitorUserScrollEnabled.tooltip') }}
            />
          </BoxForm>

          <BoxForm>
            <FlexBox
              axis='x'
              gap={1}
            >
              <PhoneIphoneOutlined fontSize='inherit' />
              <Typography>{tc('device.mobile')}</Typography>
            </FlexBox>

            <NumberField
              control={control}
              disabled={cannotUpdateAdUnit}
              fixedHelperText
              helperText={MobileRefreshIntervalSoftValidator}
              label={t('columns.refreshInterval.label')}
              name='basicSettings.mobileRefreshInterval'
              tooltip={{ content: t('columns.refreshInterval.tooltip') }}
              width='md'
            />

            <NumberField
              control={control}
              disabled={cannotUpdateAdUnit}
              fixedHelperText
              label={t('columns.adThreshold.label')}
              name='basicSettings.mobileAdThreshold'
              tooltip={{ content: t('columns.adThreshold.tooltip') }}
              width='md'
            />

            <Switch
              control={control}
              disabled={cannotUpdateAdUnit}
              label={t('columns.monitorUserScrollEnabled.label')}
              labelPlacement='start'
              name='basicSettings.mobileMonitorUserScrollEnabled'
              tooltip={{ content: t('columns.monitorUserScrollEnabled.tooltip') }}
            />
          </BoxForm>

          <BasicSettingsDomainRelated
            desktopAdThreshold={domainDesktopAdThreshold}
            desktopRefreshInterval={domainDesktopRefreshInterval}
            id={adUnit.domain.id}
            mobileAdThreshold={domainMobileAdThreshold}
            mobileRefreshInterval={domainMobileRefreshInterval}
          />
        </Stack>
      )}
    </Paper>
  )
}
